import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  X, 
  Filter,
  RotateCcw,
  Check
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DietIcon } from '@/components/ui/DietIcon';
import { 
  DIET_TYPES, 
  SPICE_LEVELS,
  CUISINE_TYPES,
  type DietType,
  type MenuSection,
  type MenuItem,
  type SpiceLevel,
  type CuisineTag
} from '@/types';
import type { 
  MenuFilterState, 
  SortOptions 
} from '@/lib/utils/searchUtils';
import { getAvailableFilterOptions, countActiveFilters } from '@/lib/utils/searchUtils';

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filters: MenuFilterState;
  onFiltersChange: (filters: Partial<MenuFilterState>) => void;
  sort: SortOptions;
  onSortChange: (sort: SortOptions) => void;
  items: MenuItem[];
  sections: MenuSection[];
  className?: string;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  items,
  sections,
}) => {
  // Local state for temporary filters (not applied until user clicks Apply)
  const [tempFilters, setTempFilters] = useState<MenuFilterState>(filters);
  
  // Sync temp filters when filters prop changes or panel opens
  useEffect(() => {
    if (isOpen) {
      setTempFilters(filters);
    }
  }, [filters, isOpen]);

  const availableOptions = getAvailableFilterOptions(items, sections);
  const activeFiltersCount = countActiveFilters(tempFilters);

  const handleApplyFilters = () => {
    onFiltersChange(tempFilters);
    onClose();
  };

  const handleDietTypeToggle = (dietType: DietType) => {
    const currentDietTypes = tempFilters.dietTypes || [];
    const newDietTypes = currentDietTypes.includes(dietType)
      ? currentDietTypes.filter(dt => dt !== dietType)
      : [...currentDietTypes, dietType];
    
    setTempFilters(prev => ({ ...prev, dietTypes: newDietTypes }));
  };

  const handleSpiceLevelToggle = (level: string) => {
    const currentLevels = tempFilters.spiceLevels || [];
    const spiceLevel = level as SpiceLevel['level'];
    const newLevels = currentLevels.includes(spiceLevel)
      ? currentLevels.filter(l => l !== level)
      : [...currentLevels, spiceLevel];
    
    setTempFilters(prev => ({ ...prev, spiceLevels: newLevels }));
  };

  const handleCuisineToggle = (cuisine: string) => {
    const currentCuisines = tempFilters.cuisineTags || [];
    const cuisineType = cuisine as CuisineTag['type'];
    const newCuisines = currentCuisines.includes(cuisineType)
      ? currentCuisines.filter(c => c !== cuisine)
      : [...currentCuisines, cuisineType];
    
    setTempFilters(prev => ({ ...prev, cuisineTags: newCuisines }));
  };

  const handlePriceRangeChange = (min: number, max: number) => {
    setTempFilters(prev => ({ ...prev, priceRange: { min, max } }));
  };

  const handlePreparationTimeRangeChange = (min: number, max: number) => {
    setTempFilters(prev => ({ ...prev, preparationTimeRange: { min, max } }));
  };

  const handleClearAllFilters = () => {
    setTempFilters({
      dietTypes: [],
      priceRange: null,
      sections: [],
      hasImage: null,
      allergens: [],
      spiceLevels: [],
      cuisineTags: [],
      customTags: [],
      isSignatureDish: null,
      isChefSpecial: null,
      preparationTimeRange: null,
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 lg:hidden"
        onClick={onClose}
      />

      {/* Filter Panel */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-xl z-50 flex flex-col"
        style={{ margin: 0, padding: 0 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <Filter className="w-5 h-5 text-gray-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
              <p className="text-sm text-gray-600">
                {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} applied
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {activeFiltersCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAllFilters}
                leftIcon={<RotateCcw className="w-4 h-4" />}
                className="text-xs"
              >
                Clear
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="p-2"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Filters Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Diet Types */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-medium text-gray-900 mb-3">Diet Type</h3>
            <div className="space-y-2">
              {Array.from(availableOptions.dietTypes).map(dietType => {
                const isSelected = tempFilters.dietTypes?.includes(dietType) || false;
                const dietInfo = DIET_TYPES[dietType];
                const count = items.filter(item => item.dietType === dietType).length;
                
                return (
                  <button
                    key={dietType}
                    onClick={() => handleDietTypeToggle(dietType)}
                    className={`
                      w-full flex items-center justify-between p-3 rounded-lg border transition-all
                      ${isSelected 
                        ? 'border-gray-300' 
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }
                    `}
                    style={isSelected ? {
                      borderColor: 'var(--theme-color)',
                      backgroundColor: 'var(--theme-color)',
                      color: 'white'
                    } : {}}
                  >
                    <div className="flex items-center space-x-3">
                      <DietIcon 
                        type={dietType === 'contains-eggs' ? 'eggs' : dietType} 
                        size={20} 
                      />
                      <span className="font-medium">{dietInfo.label}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">{count}</span>
                      {isSelected && <Check className="w-4 h-4" />}
                    </div>
                  </button>
                );
              })}

              {/* Quick Diet Filters */}
              {items.some(item => item.isVegan) && (
                <button
                  onClick={() => handleDietTypeToggle('vegan' as DietType)}
                  className={`
                    w-full flex items-center justify-between p-3 rounded-lg border transition-all
                    ${tempFilters.dietTypes?.includes('vegan' as DietType)
                      ? 'border-apple-green bg-apple-green/10 text-apple-green' 
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <DietIcon type="vegan" size={20} />
                    <span className="font-medium">Vegan</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      {items.filter(item => item.isVegan).length}
                    </span>
                    {tempFilters.dietTypes?.includes('vegan' as DietType) && <Check className="w-4 h-4" />}
                  </div>
                </button>
              )}

              {items.some(item => item.isJain) && (
                <button
                  onClick={() => handleDietTypeToggle('jain' as DietType)}
                  className={`
                    w-full flex items-center justify-between p-3 rounded-lg border transition-all
                    ${tempFilters.dietTypes?.includes('jain' as DietType)
                      ? 'border-purple-500 bg-purple-50 text-purple-600' 
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <DietIcon type="jain" size={20} />
                    <span className="font-medium">Jain</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      {items.filter(item => item.isJain).length}
                    </span>
                    {tempFilters.dietTypes?.includes('jain' as DietType) && <Check className="w-4 h-4" />}
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Spice Levels */}
          {availableOptions.spiceLevels.size > 0 && (
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-medium text-gray-900 mb-3">Spice Level</h3>
              <div className="space-y-2">
                {Array.from(availableOptions.spiceLevels).map(level => {
                  const isSelected = tempFilters.spiceLevels?.includes(level) || false;
                  const spiceInfo = SPICE_LEVELS[level];
                  const count = items.filter(item => {
                    if (item.enhancedSpiceLevel?.level) {
                      return item.enhancedSpiceLevel.level === level;
                    } else if (item.spiceLevel) {
                      const spiceLevelMap = {
                        1: 'mild', 2: 'mild', 3: 'medium', 4: 'hot', 5: 'extra-hot'
                      } as const;
                      return spiceLevelMap[item.spiceLevel as keyof typeof spiceLevelMap] === level;
                    }
                    return false;
                  }).length;
                  
                  return (
                    <button
                      key={level}
                      onClick={() => handleSpiceLevelToggle(level)}
                      className={`
                        w-full flex items-center justify-between p-3 rounded-lg border transition-all
                        ${isSelected 
                          ? 'border-orange-400 bg-orange-50 text-orange-600' 
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }
                      `}
                    >
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: spiceInfo.color }}
                        />
                        <span className="font-medium">{spiceInfo.label}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">{count}</span>
                        {isSelected && <Check className="w-4 h-4" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cuisine Types */}
          {availableOptions.cuisineTags.size > 0 && (
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-medium text-gray-900 mb-3">Cuisine</h3>
              <div className="space-y-2">
                {Array.from(availableOptions.cuisineTags).map(cuisine => {
                  const isSelected = tempFilters.cuisineTags?.includes(cuisine) || false;
                  const cuisineInfo = CUISINE_TYPES[cuisine];
                  const count = items.filter(item => 
                    item.cuisineTags?.some(tag => tag.type === cuisine)
                  ).length;
                  
                  return (
                    <button
                      key={cuisine}
                      onClick={() => handleCuisineToggle(cuisine)}
                      className={`
                        w-full flex items-center justify-between p-3 rounded-lg border transition-all
                        ${isSelected 
                          ? 'border-gray-300' 
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }
                      `}
                      style={isSelected ? {
                        borderColor: 'var(--theme-color)',
                        backgroundColor: 'var(--theme-color)',
                        color: 'white'
                      } : {}}
                    >
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: cuisineInfo.color }}
                        />
                        <span className="font-medium">{cuisineInfo.label}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">{count}</span>
                        {isSelected && <Check className="w-4 h-4" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Special Dishes */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-medium text-gray-900 mb-3">Special</h3>
            <div className="space-y-2">
              {/* Signature Dishes */}
              {availableOptions.hasSignatureDish && (
                <button
                  onClick={() => setTempFilters(prev => ({ 
                    ...prev, 
                    isSignatureDish: tempFilters.isSignatureDish === true ? null : true 
                  }))}
                  className={`
                    w-full flex items-center justify-between p-3 rounded-lg border transition-all
                    ${tempFilters.isSignatureDish === true
                      ? 'border-yellow-400 bg-yellow-50 text-yellow-600' 
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="font-medium">Signature Dishes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      {items.filter(item => item.isSignatureDish).length}
                    </span>
                    {tempFilters.isSignatureDish === true && <Check className="w-4 h-4" />}
                  </div>
                </button>
              )}

              {/* Chef's Specials */}
              {availableOptions.hasChefSpecial && (
                <button
                  onClick={() => setTempFilters(prev => ({ 
                    ...prev, 
                    isChefSpecial: tempFilters.isChefSpecial === true ? null : true 
                  }))}
                  className={`
                    w-full flex items-center justify-between p-3 rounded-lg border transition-all
                    ${tempFilters.isChefSpecial === true
                      ? 'border-purple-400 bg-purple-50 text-purple-600' 
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="font-medium">Chef's Specials</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      {items.filter(item => item.isChefSpecial).length}
                    </span>
                    {tempFilters.isChefSpecial === true && <Check className="w-4 h-4" />}
                  </div>
                </button>
              )}

              {/* With Images */}
              {availableOptions.hasImages && (
                <button
                  onClick={() => setTempFilters(prev => ({ 
                    ...prev, 
                    hasImage: tempFilters.hasImage === true ? null : true 
                  }))}
                  className={`
                    w-full flex items-center justify-between p-3 rounded-lg border transition-all
                    ${tempFilters.hasImage === true
                      ? 'border-indigo-400 bg-indigo-50 text-indigo-600' 
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="font-medium">With Photos</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      {items.filter(item => item.imageUrl).length}
                    </span>
                    {tempFilters.hasImage === true && <Check className="w-4 h-4" />}
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Price Range */}
          {availableOptions.priceRange.max > 0 && (
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-medium text-gray-900 mb-3">Price Range</h3>
              <PriceRangeSlider
                min={availableOptions.priceRange.min}
                max={availableOptions.priceRange.max}
                value={tempFilters.priceRange || { min: availableOptions.priceRange.min, max: availableOptions.priceRange.max }}
                onChange={handlePriceRangeChange}
              />
            </div>
          )}

          {/* Preparation Time Range */}
          {availableOptions.preparationTimeRange.max > 0 && (
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-medium text-gray-900 mb-3">Preparation Time</h3>
              <PreparationTimeSlider
                min={availableOptions.preparationTimeRange.min}
                max={availableOptions.preparationTimeRange.max}
                value={tempFilters.preparationTimeRange || { min: availableOptions.preparationTimeRange.min, max: availableOptions.preparationTimeRange.max }}
                onChange={handlePreparationTimeRangeChange}
              />
            </div>
          )}
        </div>

        {/* Apply Button */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <Button
            onClick={handleApplyFilters}
            className="w-full"
            size="lg"
            style={{ backgroundColor: 'var(--theme-color)' }}
          >
            Apply Filters ({activeFiltersCount})
          </Button>
        </div>
      </motion.div>
    </>
  );
};

// Dual Range Slider Component
const DualRangeSlider: React.FC<{
  min: number;
  max: number;
  value: { min: number; max: number };
  onChange: (min: number, max: number) => void;
  formatValue: (value: number) => string;
}> = ({ min, max, value, onChange, formatValue }) => {
  const [localMin, setLocalMin] = useState(value.min);
  const [localMax, setLocalMax] = useState(value.max);
  const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);
  const sliderRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalMin(value.min);
    setLocalMax(value.max);
  }, [value]);

  const handleRangeChange = (newMin: number, newMax: number) => {
    setLocalMin(newMin);
    setLocalMax(newMax);
    onChange(newMin, newMax);
  };

  const percentage1 = ((localMin - min) / (max - min)) * 100;
  const percentage2 = ((localMax - min) / (max - min)) * 100;

  const getValueFromPosition = (clientX: number) => {
    if (!sliderRef.current) return min;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    return Math.round(min + (percentage / 100) * (max - min));
  };

  const handleSliderClick = (e: React.MouseEvent) => {
    const clickValue = getValueFromPosition(e.clientX);
    
    // Determine which thumb is closer to the click
    const distanceToMin = Math.abs(clickValue - localMin);
    const distanceToMax = Math.abs(clickValue - localMax);
    
    if (distanceToMin <= distanceToMax) {
      // Update min value
      const newMin = Math.min(clickValue, localMax - 1);
      handleRangeChange(newMin, localMax);
    } else {
      // Update max value
      const newMax = Math.max(clickValue, localMin + 1);
      handleRangeChange(localMin, newMax);
    }
  };

  const handleMouseDown = (thumb: 'min' | 'max') => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(thumb);

    const handleMouseMove = (e: globalThis.MouseEvent) => {
      const newValue = getValueFromPosition(e.clientX);
      
      if (thumb === 'min') {
        const newMin = Math.min(newValue, localMax - 1);
        handleRangeChange(newMin, localMax);
      } else {
        const newMax = Math.max(newValue, localMin + 1);
        handleRangeChange(localMin, newMax);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (thumb: 'min' | 'max') => (e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(thumb);

    const handleTouchMove = (e: globalThis.TouchEvent) => {
      const touch = e.touches[0];
      const newValue = getValueFromPosition(touch.clientX);
      
      if (thumb === 'min') {
        const newMin = Math.min(newValue, localMax - 1);
        handleRangeChange(newMin, localMax);
      } else {
        const newMax = Math.max(newValue, localMin + 1);
        handleRangeChange(localMin, newMax);
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(null);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>{formatValue(localMin)}</span>
        <span>{formatValue(localMax)}</span>
      </div>
      
      <div 
        ref={sliderRef}
        className="relative h-8 flex items-center cursor-pointer"
        onClick={handleSliderClick}
      >
        {/* Track */}
        <div className="absolute w-full h-2 bg-gray-200 rounded-lg" />
        
        {/* Active Range */}
        <div 
          className="absolute h-2 rounded-lg"
          style={{
            left: `${percentage1}%`,
            width: `${percentage2 - percentage1}%`,
            backgroundColor: 'var(--theme-color)'
          }}
        />
        
        {/* Min Thumb */}
        <div
          className="absolute w-5 h-5 border-2 border-white rounded-full shadow-lg cursor-grab active:cursor-grabbing transform -translate-x-1/2 hover:scale-110 transition-transform z-20"
          style={{ 
            left: `${percentage1}%`,
            backgroundColor: 'var(--theme-color)',
            transform: `translateX(-50%) ${isDragging === 'min' ? 'scale(1.2)' : 'scale(1)'}`
          }}
          onMouseDown={handleMouseDown('min')}
          onTouchStart={handleTouchStart('min')}
          onClick={(e) => e.stopPropagation()}
        />
        
        {/* Max Thumb */}
        <div
          className="absolute w-5 h-5 border-2 border-white rounded-full shadow-lg cursor-grab active:cursor-grabbing transform -translate-x-1/2 hover:scale-110 transition-transform z-20"
          style={{ 
            left: `${percentage2}%`,
            backgroundColor: 'var(--theme-color)',
            transform: `translateX(-50%) ${isDragging === 'max' ? 'scale(1.2)' : 'scale(1)'}`
          }}
          onMouseDown={handleMouseDown('max')}
          onTouchStart={handleTouchStart('max')}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

// Price Range Slider Component
const PriceRangeSlider: React.FC<{
  min: number;
  max: number;
  value: { min: number; max: number };
  onChange: (min: number, max: number) => void;
}> = ({ min, max, value, onChange }) => {
  return (
    <DualRangeSlider
      min={min}
      max={max}
      value={value}
      onChange={onChange}
      formatValue={(val) => `${val}`}
    />
  );
};

// Preparation Time Slider Component
const PreparationTimeSlider: React.FC<{
  min: number;
  max: number;
  value: { min: number; max: number };
  onChange: (min: number, max: number) => void;
}> = ({ min, max, value, onChange }) => {
  return (
    <DualRangeSlider
      min={min}
      max={max}
      value={value}
      onChange={onChange}
      formatValue={(val) => `${val} min`}
    />
  );
};